import React from 'react'
import { useList } from '@refinedev/core'
import { Table } from 'antd'
import { brand } from '../../theme/brand'
import { TableSurface, EmptyHint } from '../../theme/listkit'
import { monthLabel, groupNominations, type NominationRow, type NominationGroup } from './botm'

/** The FK embed pulling the business name and the nominator's profile inline. */
const SELECT = '*, business:businesses(id,name), nominator:profiles!botm_nominations_nominator_profile_fkey(name,class_year)'

/**
 * Nominations tab — every member nomination for the selected month, grouped by
 * business so the committee sees who's in the running without scrolling a raw
 * list. Read-only: nominations flow in from the app; nothing here writes.
 */
export function NominationsTab({ month }: { month: string }) {
  const { result, query } = useList<NominationRow>({
    resource: 'botm_nominations',
    filters: [{ field: 'month', operator: 'eq', value: month }],
    pagination: { pageSize: 500 },
    meta: { select: SELECT },
  })

  const groups = React.useMemo(() => groupNominations(result?.data ?? []), [result])

  if (!query.isLoading && groups.length === 0) {
    return <EmptyHint>No nominations for {monthLabel(month)} yet.</EmptyHint>
  }

  return (
    <TableSurface>
      <Table<NominationGroup>
        dataSource={groups}
        loading={query.isLoading}
        rowKey="businessId"
        pagination={false}
        scroll={{ x: true }}
      >
        <Table.Column<NominationGroup>
          dataIndex="businessName"
          title="Business"
          render={(name: string) => (
            <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 13.5, color: brand.ink }}>
              {name}
            </span>
          )}
        />
        <Table.Column<NominationGroup>
          dataIndex="count"
          title="Nominations"
          render={(count: number) => (
            <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 13, color: brand.ink }}>{count}</span>
          )}
        />
        <Table.Column<NominationGroup>
          dataIndex="nominators"
          title="Nominated by"
          render={(_: unknown, record) => {
            const shown = record.nominators.slice(0, 2)
            const extra = record.nominators.length - shown.length
            return (
              <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.ink }}>
                {shown.join(', ')}
                {extra > 0 ? ` +${extra}` : ''}
                {record.hasSelf ? ' (includes self)' : ''}
              </span>
            )
          }}
        />
        <Table.Column<NominationGroup>
          dataIndex="latestReason"
          title="Why (latest)"
          render={(reason: string) => (
            <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.sub }}>
              {reason}
            </span>
          )}
        />
      </Table>
    </TableSurface>
  )
}
